import * as React from "react";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipsEnabledProvider,
  useTooltipsEnabled,
} from "~/components/ui/tooltip";

type Side = React.ComponentProps<typeof TooltipContent>["side"];

/**
 * FRONT-1192: the one-line way to give any element a real tooltip instead of
 * the browser's native `title` attribute (~1s delay, OS chrome that ignores
 * our theme, invisible on touch).
 *
 * `<TooltipProvider>` is already mounted at the app root (`main.tsx`), so this
 * needs no setup at the call site. A falsy `label` renders the child untouched,
 * which is what makes conditional hints (`label={reason}`) safe.
 *
 * The child must be a single element that forwards a ref and DOM props — the
 * trigger is composed onto it via Radix `asChild` rather than adding a wrapper,
 * so layout is unchanged. For text nodes, fragments, or a `disabled` control
 * (which swallows the pointer events the trigger needs), wrap the child in a
 * span yourself — `Button` does exactly that for its disabled case.
 *
 * ponytail: deliberately does NOT survive being cloned by another `asChild`
 * trigger. `<DropdownMenuTrigger asChild><Tip label="…"><button/></Tip></DropdownMenuTrigger>`
 * LOOKS right but is broken: `Tip` is a plain function component, not a
 * Radix Slot, so when the outer trigger clones its child it hands its own
 * merged props (onClick, aria-expanded, ref, …) to `Tip` as ordinary props —
 * and `Tip`'s signature only reads `label`/`side`/`children`, silently
 * dropping the rest. The failure is easy to miss by eye: the button still
 * focuses and shows a hover ring, but the menu/popover never opens
 * (confirmed the hard way via a `@verify` browser run — FRONT-1339).
 *
 * When ONE element needs both a hover tooltip and to serve as another
 * `asChild` trigger's target, don't reach for `Tip` at all — compose the raw
 * `Tooltip`/`TooltipTrigger` primitives directly, with `Tooltip` OUTERMOST
 * and `TooltipTrigger asChild` wrapping the other trigger (`PopoverTrigger
 * asChild` / `DropdownMenuTrigger asChild`), which wraps the real button.
 * Both are genuine Radix Slot-based primitives, so their props merge
 * correctly all the way down to the button. `note-row.tsx`'s Move control is
 * the browser-verified reference for this shape.
 */
/**
 * FRONT-2267: the tooltips on/off switch is enforced on the `Tooltip`
 * PRIMITIVE (`components/ui/tooltip.tsx`), not here — see its own docblock
 * for why a wrapper-level gate was the wrong altitude. `Tip` reads the same
 * switch purely as a shortcut: with tooltips off there is no reason to mount
 * Radix at all, so it returns the bare child. Deleting this line would change
 * nothing a person can see.
 *
 * Switching tooltips off must never remove a control's NAME: `aria-label`
 * lives on the control itself and is untouched by any of this. Nor may it
 * hide the only explanation of a disabled control — a reason a person must
 * act on belongs in visible text, which is a rule about the call site, not
 * about this gate.
 */
function Tip({
  label,
  side,
  children,
}: {
  label?: React.ReactNode;
  side?: Side;
  children: React.ReactElement;
}) {
  const tooltipsEnabled = useTooltipsEnabled();

  // Any falsy label no-ops, not just undefined/null/"". `label={cond && 'text'}`
  // yields `false`, which would otherwise render an empty tooltip bubble.
  // With tooltips switched off, an absent label and a present one are the
  // same thing — one condition, so the two cases cannot drift apart.
  if (!label || !tooltipsEnabled) return children;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side={side}>{label}</TooltipContent>
    </Tooltip>
  );
}

export { Tip, TooltipsEnabledProvider, useTooltipsEnabled };
